"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import { Formik, Form, Field, FormikHelpers } from "formik"
import * as Yup from "yup"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { useToast } from "@/hooks/use-toast"
import { format } from "date-fns"
import {
  CalendarIcon,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ShieldCheck,
  Mail,
  Building2,
  MapPin,
  User,
  Tag,
  MessageSquare,
  Loader2,
  ArrowLeft,
  AtSign,
  Globe,
  Server,
  Zap,
  Plus,
  ChevronLeft,
  X,
  ChevronRight,
  AlertCircle,
  CornerDownLeft,
} from "lucide-react"
import { apiFetch } from "@/lib/api/apiFetch"
import { HybridSelect } from "./hybridSelect"

// ─── Types ────────────────────────────────────────────────────────────────────

interface ContactDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onContactCreated?: (contact: any) => void
  onContactUpdated?: (contact: any) => void
  mode?: "create" | "edit"
  contact?: any
  statusOptionsList: Array<{ value: string; label: string }>
  sourceOptionsList: Array<{ value: string; label: string }>
}

interface ContactFormValues {
  firstName: string
  lastName: string
  emails: string[]
  email?: string
  company: string
  city: string
  state: string
  source: string
  status: string
  dateOfLastContact: string
  interests: string
  role: string
  remarks: string
  phone: string
}

interface EmailVerifyResult {
  email: string
  risk_score: number
  verdict: string
  suggestion: string | null
  is_deliverable: boolean | null
  checks: {
    syntax: { valid: boolean; username: string; domain: string; error: string | null }
    disposable: { is_disposable: boolean; method: string; confidence: number }
    typo: { suggestion: string | null; distance: number | null }
    mx: { has_mx: boolean; records: string[]; error: string | null }
    smtp: any
    provider: any
  }
  cached: boolean
  checked_at: string
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ContactSchema = Yup.object().shape({
  firstName: Yup.string().min(1).max(50).required("First name is required"),
  lastName: Yup.string(),
  emails: Yup.array()
    .of(Yup.string().email("Please enter a valid email address"))
    .min(1, "At least one email is required")
    .max(5, "Maximum 5 emails allowed")
    .required("Email is required"),
  company: Yup.string().max(100),
  city: Yup.string().max(100),
  state: Yup.string().max(100),
  source: Yup.string().required("Please select a source"),
  status: Yup.string().required("Please select a status"),
  dateOfLastContact: Yup.string(),
  interests: Yup.string(),
  role: Yup.string().oneOf(["dealer", "user", "partner"]).required("Please select a role"),
  remarks: Yup.string().max(500).trim(),
  phone: Yup.string().max(20).trim(),
})

const initialValues: ContactFormValues = {
  firstName: "",
  lastName: "",
  emails: [],
  company: "",
  city: "",
  state: "",
  source: "",
  status: "",
  dateOfLastContact: "",
  interests: "",
  role: "user",
  remarks: "",
  phone: "",
}

const roleOptions = [
  { value: "user", label: "User" },
  { value: "dealer", label: "Dealer" },
  { value: "partner", label: "Partner" },
]

const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === "object" && error !== null) {
    const maybeMessage = (error as { message?: unknown }).message
    if (typeof maybeMessage === "string" && maybeMessage.trim()) return maybeMessage
  }
  return fallback
}

// ─── ContactFormBody ──────────────────────────────────────────────────────────

interface ContactFormBodyProps {
  values: ContactFormValues
  errors: any
  touched: any
  handleChange: any
  setFieldValue: any
  resetForm: () => void
  isSubmitting: boolean
  onCancel: () => void
  mode: "create" | "edit"
  statusOptionsList: Array<{ value: string; label: string }>
  sourceOptionsList: Array<{ value: string; label: string }>
}

// ── Section Divider ────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center pt-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
        {children}
      </h3>
    </div>
  )
}

function Divider() {
  return (
    <div className="relative">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-slate-200 dark:border-slate-800" />
      </div>
    </div>
  )
}

// ── ContactFormBody ────────────────────────────────────────────────────────────

export function ContactFormBody({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  resetForm,
  isSubmitting,
  onCancel,
  mode,
  statusOptionsList,
  sourceOptionsList,
}: ContactFormBodyProps) {
  const chipsScrollRef = useRef<HTMLDivElement>(null)
  const [localEmailInput, setLocalEmailInput] = useState("")
  const [emailErr, setEmailErr] = useState("")
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)

  const fieldErr = (name: keyof ContactFormValues) =>
    errors[name] && touched[name]
      ? "border-destructive focus-visible:ring-destructive"
      : ""

  useEffect(() => {
    const el = chipsScrollRef.current
    if (!el) return

    const handleWheel = (e: WheelEvent) => {
      // Only intercept and scroll if the container is actually scrollable
      if (el.scrollWidth > el.clientWidth) {
        e.preventDefault()
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
          el.scrollLeft += e.deltaX
        } else {
          el.scrollLeft += e.deltaY
        }
      }
    }

    el.addEventListener("wheel", handleWheel, { passive: false })
    return () => {
      el.removeEventListener("wheel", handleWheel)
    }
  }, [values.emails.length])

  // ── Email helpers ────────────────────────────────────────────────────────────

  function addEmail() {
    const val = localEmailInput.trim().toLowerCase()
    if (!val) return
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      setEmailErr("Enter a valid email address")
      return
    }
    if (values.emails.includes(val)) {
      setEmailErr("Already added")
      return
    }
    if (values.emails.length >= 5) {
      setEmailErr("Maximum 5 emails allowed")
      return
    }
    setFieldValue("emails", [...values.emails, val])
    setLocalEmailInput("")
    setEmailErr("")
    setTimeout(() => {
      if (chipsScrollRef.current) {
        chipsScrollRef.current.scrollLeft = 9999
        updateArrows()
      }
    }, 60)
  }

  function removeEmail(i: number) {
    setFieldValue("emails", values.emails.filter((_, idx) => idx !== i))
    setTimeout(updateArrows, 60)
  }

  function scrollChips(dir: number) {
    if (chipsScrollRef.current) {
      chipsScrollRef.current.scrollLeft += dir * 180
      setTimeout(updateArrows, 220)
    }
  }

  function updateArrows() {
    const s = chipsScrollRef.current
    if (!s) return
    setShowLeftArrow(s.scrollLeft > 4)
    setShowRightArrow(s.scrollLeft < s.scrollWidth - s.clientWidth - 4)
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3">
      <Divider />
      {/* ── IDENTITY ── */}
      <SectionLabel>Identity</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* First Name */}
        <div className="space-y-1.5">
          <Label htmlFor="firstName" className="text-xs text-muted-foreground">
            First name <span className="text-destructive">*</span>
          </Label>
          <Field
            as={Input}
            id="firstName"
            name="firstName"
            placeholder="First name"
            className={`h-9 text-sm ${fieldErr("firstName")}`}
          />
          {errors.firstName && touched.firstName && (
            <p className="text-xs text-destructive">{errors.firstName}</p>
          )}
        </div>

        {/* Last Name */}
        <div className="space-y-1.5">
          <Label htmlFor="lastName" className="text-xs text-muted-foreground">Last name</Label>
          <Field
            as={Input}
            id="lastName"
            name="lastName"
            placeholder="Last name"
            className="h-9 text-sm"
          />
        </div>

        {/* Phone */}
        <div className="space-y-1.5">
          <Label htmlFor="phone" className="text-xs text-muted-foreground">Phone</Label>
          <Field
            as={Input}
            id="phone"
            name="phone"
            placeholder="Enter phone number"
            className={`h-9 text-sm ${fieldErr("phone")}`}
          />
          {errors.phone && touched.phone && (
            <p className="text-xs text-destructive">{errors.phone}</p>
          )}
        </div>
      </div>

      <Divider />

      {/* ── EMAIL ADDRESSES ── */}
      <SectionLabel>Email addresses</SectionLabel>

      <div className="space-y-2 w-full overflow-hidden">
        {mode === "edit" ?
          <>
            <Field
              as={Input}
              value={values.emails[0]}
              id="email"
              name="email"
              placeholder="Enter email"
              className="h-9 text-sm"
              disabled
            />
          </>
          : (
            <>
              {/* Input row */}
              < div className="flex items-start gap-2 w-full">
                <Input
                  type="email"
                  placeholder={
                    values.emails.length >= 5
                      ? "Max 5 reached"
                      : "Enter email and press Enter or +"
                  }
                  value={localEmailInput}
                  disabled={values.emails.length >= 5}
                  onChange={(e) => {
                    setLocalEmailInput(e.target.value)

                    if (emailErr) {
                      setEmailErr("")
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      addEmail()
                    }
                  }}
                  className={`
        h-9 text-sm flex-1
        ${emailErr
                      ? "border-destructive focus-visible:ring-destructive"
                      : ""}
            `}
                />

                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={addEmail}
                  disabled={values.emails.length >= 5}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Chips row */}
              <div className="w-full overflow-hidden">

                <div className="grid grid-cols-[28px,minmax(0,1fr),28px] items-center gap-1.5 w-full">

                  {/* Left Arrow */}
                  <div className="flex items-center justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={`
            h-7 w-7 rounded-full shrink-0
            transition-all duration-200
            ${showLeftArrow && values.emails.length > 0
                          ? "opacity-100"
                          : "opacity-0 pointer-events-none"}
          `}
                      onClick={() => scrollChips(-1)}
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {/* Chips Container */}
                  <div className="relative w-full min-w-0 overflow-hidden">

                    {/* Fade Left */}
                    {showLeftArrow && (
                      <div className="absolute left-0 top-0 bottom-0 w-6 z-10 bg-gradient-to-r from-background to-transparent pointer-events-none" />
                    )}

                    {/* Fade Right */}
                    {showRightArrow && (
                      <div className="absolute right-0 top-0 bottom-0 w-6 z-10 bg-gradient-to-l from-background to-transparent pointer-events-none" />
                    )}

                    {values.emails.length === 0 ? (

                      <div className="h-[20px] flex items-center">
                        <p className="text-xs text-muted-foreground italic px-1">
                          No emails added yet
                        </p>
                      </div>
                    ) : (

                      <div
                        ref={chipsScrollRef}
                        onScroll={updateArrows}
                        className="
                    flex gap-2
                    w-full
                    overflow-x-auto
                    overflow-y-hidden
                    scroll-smooth
                    py-1 px-0.5
                    hide-scrollbar
                  "
                        style={{
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                        }}
                      >

                        {values.emails.filter(Boolean).map((email, i) => (

                          <div
                            key={i}
                            className="
                        inline-flex items-center gap-2
                        shrink-0
                        h-9
                        rounded-full
                        border border-border
                        bg-muted/60
                        pl-4 pr-2
                        transition-all
                        hover:bg-muted
                      "
                          >

                            {/* Email */}
                            <span
                              className="
                          text-sm
                          font-medium
                          text-foreground
                          whitespace-nowrap
                        "
                            >
                              {email}
                            </span>

                            {/* Remove Button */}
                            <button
                              type="button"
                              onClick={() => removeEmail(i)}
                              className="
                          flex items-center justify-center
                          shrink-0
                          w-5 h-5
                          rounded-full
                          text-muted-foreground
                          hover:text-destructive
                          hover:bg-destructive/10
                          transition-colors
                        "
                            >
                              <X className="w-3 h-3" />
                            </button>

                          </div>
                        ))}

                      </div>

                    )}

                  </div>

                  {/* Right Arrow */}
                  <div className="flex items-center justify-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={`
            h-7 w-7 rounded-full shrink-0
            transition-all duration-200
            ${showRightArrow && values.emails.length > 0
                          ? "opacity-100"
                          : "opacity-0 pointer-events-none"}
          `}
                      onClick={() => scrollChips(1)}
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                </div>

              </div>

              {/* Footer */}
              <div className="flex items-center justify-between gap-2">

                <div className="min-w-0 flex-1">

                  {emailErr ? (

                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 shrink-0" />
                      <span className="truncate">
                        {emailErr}
                      </span>
                    </p>

                  ) : (

                    <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <CornerDownLeft className="h-3 w-3 shrink-0" />
                      <span className="truncate">
                        Press Enter to add · max 5
                      </span>
                    </p>

                  )}

                </div>

                <span className="
      text-[11px]
      text-muted-foreground
      bg-muted
      border border-border
      rounded-full
      px-2 py-0.5
      shrink-0
    ">
                  {values.emails.length} / 5
                </span>

              </div>
            </>
          )}
      </div>

      <Divider />

      {/* ── LOCATION & COMPANY ── */}
      <SectionLabel>Location &amp; company</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="city" className="text-xs text-muted-foreground">City</Label>
          <Field
            as={Input}
            id="city"
            name="city"
            placeholder="City"
            className="h-9 text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="state" className="text-xs text-muted-foreground">State</Label>
          <Field
            as={Input}
            id="state"
            name="state"
            placeholder="State"
            className="h-9 text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="company" className="text-xs text-muted-foreground">Company</Label>
          <Field
            as={Input}
            id="company"
            name="company"
            placeholder="Company name"
            className="h-9 text-sm"
          />
        </div>
      </div>

      <Divider />

      {/* ── CLASSIFICATION ── */}
      <SectionLabel>Classification</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Status */}
        <div className="space-y-1.5">
          <HybridSelect
            label="Status"
            value={values.status}
            onChange={(v) => setFieldValue("status", v)}
            options={statusOptionsList}
            error={errors.status}
            touched={touched.status}
          />
        </div>

        {/* Source */}
        <div className="space-y-1.5">
          <HybridSelect
            label="Source"
            value={values.source}
            onChange={(v) => setFieldValue("source", v)}
            options={sourceOptionsList}
            error={errors.source}
            touched={touched.source}
          />
        </div>

        {/* Role */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">
            Role <span className="text-destructive">*</span>
          </Label>
          <Select
            value={values.role}
            onValueChange={(v) => handleChange({ target: { name: "role", value: v } })}
          >
            <SelectTrigger
              className={`h-9 text-sm ${fieldErr("role")}`}
            >
              <SelectValue placeholder="Select role" />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.role && touched.role && (
            <p className="text-xs text-destructive">{errors.role}</p>
          )}
        </div>
      </div>

      <Divider />

      {/* ── ADDITIONAL ── */}
      <SectionLabel>Additional</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Date of Last Contact */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Date of last contact</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={`h-9 w-full justify-start text-left text-sm font-normal
                  ${!values.dateOfLastContact && "text-muted-foreground"}`}
              >
                <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                {values.dateOfLastContact
                  ? format(new Date(values.dateOfLastContact), "MM-dd-yyyy")
                  : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={
                  values.dateOfLastContact
                    ? new Date(values.dateOfLastContact)
                    : undefined
                }
                onSelect={(date) =>
                  setFieldValue(
                    "dateOfLastContact",
                    date ? format(date, "yyyy-MM-dd") : ""
                  )
                }
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Interests */}
        <div className="space-y-1.5">
          <Label htmlFor="interests" className="text-xs text-muted-foreground">
            Interests
          </Label>
          <Field
            as={Input}
            id="interests"
            name="interests"
            placeholder="Enter interests"
            className="h-9 text-sm"
          />
        </div>
      </div>

      {/* Remarks */}
      <div className="space-y-1.5 mb-2">
        <Label htmlFor="remarks" className="text-xs text-muted-foreground">Remarks</Label>
        <Field
          as={Textarea}
          id="remarks"
          name="remarks"
          placeholder="Add notes or remarks…"
          rows={3}
          className={`text-sm resize-none ${fieldErr("remarks")}`}
        />
        {errors.remarks && touched.remarks && (
          <p className="text-xs text-destructive">{errors.remarks}</p>
        )}
      </div>

      {/* ── ACTIONS ── */}
      <div className="flex justify-end gap-2 pt-4 border-t border-border mt-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={isSubmitting}
          className="h-9 px-4 text-sm"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={isSubmitting || Object.keys(errors).length > 0}
          className="h-9 px-5 text-sm min-w-[140px]"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              Verifying…
            </>
          ) : mode === "edit" ? (
            "Update contact"
          ) : (
            <>
              <ShieldCheck className="mr-2 h-3.5 w-3.5" />
              Verify contact
            </>
          )}
        </Button>
      </div>
    </div >
  )
}
// ─── VerifyPreview ────────────────────────────────────────────────────────────

interface VerifyPreviewProps {
  values: ContactFormValues
  verifyData: EmailVerifyResult
  statusOptionsList: Array<{ value: string; label: string }>
  sourceOptionsList: Array<{ value: string; label: string }>
  onBack: () => void
  onConfirm: () => void
  isSubmitting: boolean
}

interface VerifyPreviewProps {
  values: ContactFormValues
  verifyData: EmailVerifyResult
  statusOptionsList: Array<{ value: string; label: string }>
  sourceOptionsList: Array<{ value: string; label: string }>
  onBack: () => void
  onConfirm: () => void
  isSubmitting: boolean
}

function VerifyPreview({
  values,
  verifyData,
  statusOptionsList,
  sourceOptionsList,
  onBack,
  onConfirm,
  isSubmitting,
}: VerifyPreviewProps) {


  const verdictConfig: Record<string, { color: string; bg: string; border: string; icon: React.ReactNode; label: string }> = {
    safe: {
      color: "text-emerald-600 dark:text-emerald-400",
      bg: "bg-emerald-50 dark:bg-emerald-950/40",
      border: "border-emerald-200 dark:border-emerald-800",
      icon: <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />,
      label: "Safe",
    },
    risky: {
      color: "text-amber-600 dark:text-amber-400",
      bg: "bg-amber-50 dark:bg-amber-950/40",
      border: "border-amber-200 dark:border-amber-800",
      icon: <AlertTriangle className="h-5 w-5 text-amber-500 dark:text-amber-400" />,
      label: "Risky",
    },
    invalid: {
      color: "text-red-600 dark:text-red-400",
      bg: "bg-red-50 dark:bg-red-950/40",
      border: "border-red-200 dark:border-red-800",
      icon: <XCircle className="h-5 w-5 text-red-500 dark:text-red-400" />,
      label: "Invalid",
    },
  }

  const verdict = verifyData ? (verdictConfig[verifyData.verdict] ?? verdictConfig["risky"]) : verdictConfig["risky"]

  const sourceName = sourceOptionsList.find((s) => s.value === values.source)?.label ?? values.source
  const statusName = statusOptionsList.find((s) => s.value === values.status)?.label ?? values.status

  const CheckRow = ({
    icon,
    label,
    value,
    sub,
  }: {
    icon: React.ReactNode
    label: string
    value: React.ReactNode
    sub?: string
  }) => (
    <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-0">
      <div className="mt-0.5 shrink-0 text-muted-foreground">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
        <div className="text-sm font-medium text-foreground mt-0.5">{value}</div>
        {sub && <p className="text-xs text-muted-foreground/70 mt-0.5">{sub}</p>}
      </div>
    </div>
  )

  const RiskBar = ({ score }: { score: number }) => {
    const color = score === 0 ? "bg-emerald-500" : score < 40 ? "bg-amber-400" : "bg-red-500"
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${Math.max(score, 3)}%` }} />
        </div>
        <span className="text-xs font-semibold text-muted-foreground w-6 text-right">{score}</span>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* ── Contact Summary Card ── */}
      <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        {/* Header strip — intentionally stays dark in both modes for contrast */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 dark:from-slate-900 dark:to-slate-800 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-white/10 flex items-center justify-center ring-2 ring-white/20 shrink-0">
              <span className="text-white font-bold text-base leading-none">
                {values.firstName[0]?.toUpperCase()}{values.lastName[0]?.toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-white font-semibold text-base leading-tight">
                {values.firstName} {values.lastName}
              </p>
              <p className="text-slate-300 text-sm">{values.email}</p>
            </div>
          </div>
        </div>

        {/* Detail grid */}
        <div className="px-5 py-3 grid grid-cols-2 gap-x-6 gap-y-0 divide-y divide-border">
          <CheckRow icon={<Building2 className="h-4 w-4" />} label="Company" value={values.company} />
          <CheckRow icon={<MapPin className="h-4 w-4" />} label="Location" value={`${values.city}, ${values.state}`} />
          <CheckRow icon={<User className="h-4 w-4" />} label="Role" value={<span className="capitalize">{values.role}</span>} />
          <CheckRow icon={<Tag className="h-4 w-4" />} label="Source" value={sourceName} />
          <CheckRow icon={<Zap className="h-4 w-4" />} label="Status" value={statusName} />
          {values.interests && (
            <CheckRow icon={<Tag className="h-4 w-4" />} label="Interests" value={values.interests} />
          )}
          {values.dateOfLastContact && (
            <CheckRow
              icon={<CalendarIcon className="h-4 w-4" />}
              label="Last Contact"
              value={format(new Date(values.dateOfLastContact), "MMM d, yyyy")}
            />
          )}
          {values.remarks && (
            <div className="col-span-2">
              <CheckRow icon={<MessageSquare className="h-4 w-4" />} label="Remarks" value={values.remarks} />
            </div>
          )}
        </div>
      </div>

      {/* ── Email Verification Card ── */}
      {verifyData && (
        <div className={`rounded-xl border ${verdict.border} ${verdict.bg} overflow-hidden`}>
          {/* Verdict header */}
          <div className="px-5 py-3.5 flex items-center justify-between border-b border-border/50">
            <div className="flex items-center gap-2.5">
              {verdict.icon}
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Email Verification</p>
                <p className={`font-bold text-base ${verdict.color}`}>{verdict.label}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground mb-1">Risk Score</p>
              <div className="w-28">
                <RiskBar score={verifyData.risk_score} />
              </div>
            </div>
          </div>

          {/* Checks grid */}
          <div className="px-5 py-3 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
            {/* Syntax */}
            <CheckRow
              icon={<AtSign className="h-4 w-4" />}
              label="Syntax"
              value={
                verifyData.checks.syntax.valid ? (
                  <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Valid
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
                    <XCircle className="h-3.5 w-3.5" /> Invalid
                  </span>
                )
              }
              sub={`${verifyData.checks.syntax.username} @ ${verifyData.checks.syntax.domain}`}
            />

            {/* Disposable */}
            <CheckRow
              icon={<Mail className="h-4 w-4" />}
              label="Disposable"
              value={
                verifyData.checks.disposable.is_disposable ? (
                  <span className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
                    <XCircle className="h-3.5 w-3.5" /> Disposable address
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Not disposable
                  </span>
                )
              }
              sub={`Confidence ${Math.round(verifyData.checks.disposable.confidence * 100)}%`}
            />

            {/* MX Records */}
            <CheckRow
              icon={<Server className="h-4 w-4" />}
              label="MX Records"
              value={
                verifyData.checks.mx.has_mx ? (
                  <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Found
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
                    <XCircle className="h-3.5 w-3.5" /> Not found
                  </span>
                )
              }
              sub={verifyData.checks.mx.records?.[0] ?? undefined}
            />

            {/* Typo */}
            <CheckRow
              icon={<Globe className="h-4 w-4" />}
              label="Typo Check"
              value={
                verifyData.checks.typo.suggestion ? (
                  <span className="text-amber-600 dark:text-amber-400">Did you mean: {verifyData.checks.typo.suggestion}?</span>
                ) : (
                  <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-3.5 w-3.5" /> No suggestions
                  </span>
                )
              }
            />
          </div>

          {/* Footer meta */}
          <div className="px-5 py-2.5 border-t border-border/50 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Checked {format(new Date(verifyData.checked_at), "MMM d, yyyy · h:mm a")}
            </p>
            {verifyData.cached && (
              <Badge variant="secondary" className="text-xs px-2 py-0.5">Cached</Badge>
            )}
          </div>
        </div>
      )}

      {/* ── Actions ── */}
      <div className="flex justify-between pt-1">
        <Button type="button" variant="outline" onClick={onBack} disabled={isSubmitting} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Form
        </Button>
        <Button
          type="button"
          onClick={onConfirm}
          disabled={isSubmitting}
          className="min-w-[150px] gap-2 bg-slate-800 hover:bg-slate-700 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-slate-100 text-white"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Creating…
            </>
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4" />
              Create Contact
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

// ─── ContactDialog ────────────────────────────────────────────────────────────

export function ContactDialog({
  open,
  onOpenChange,
  onContactCreated,
  onContactUpdated,
  mode = "create",
  contact,
  statusOptionsList,
  sourceOptionsList,
}: ContactDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [verifyResponse, setVerifyResponse] = useState<EmailVerifyResult | null>(null)
  const [pendingValues, setPendingValues] = useState<ContactFormValues | null>(null)
  const { toast } = useToast()

  // Reset verify state when dialog opens/closes or mode changes
  useEffect(() => {
    if (!open) {
      setVerifyResponse(null)
      setPendingValues(null)
    }
  }, [open])

  const getInitialValues = (): ContactFormValues => {
    if (mode === "edit" && contact) {
      return {
        firstName: contact.firstName || "",
        lastName: contact.lastName || "",
        emails: contact.email ? [contact.email] : [""],
        company: contact.company || "",
        city: contact.city || "",
        state: contact.state || "",
        source: contact.source?._id || "",
        status: contact.status?._id || "",
        dateOfLastContact: contact.dateOfLastContact || "",
        interests: contact.interests || "",
        role: contact.role || "",
        remarks: contact.remarks || "",
        phone: contact.phone || "",
      }
    }
    return initialValues
  }

  // Called when Formik submits — different behaviour per mode
  const handleSubmit = async (values: ContactFormValues, { resetForm, setSubmitting }: FormikHelpers<ContactFormValues>) => {
    setIsSubmitting(true)

    const payload = {
      ...values,
      dateOfLastContact: new Date(values.dateOfLastContact).getTime() || undefined,
    }
    try {
      const url = `/api/contacts`
      const method = mode === "edit" && contact?._id ? "PUT" : "POST"

      const body = mode === "edit" && contact?._id
        ? { id: contact._id, ...payload }
        : payload

      const result = await apiFetch(url, {
        method,
        data: body,
      })
      // if (!result.error) 

      if (result.error === false) {
        toast({
          title: "Success",
          description: `Contact ${mode === "edit" ? "updated" : "created"} successfully`,
        })

        // Call appropriate callback based on mode
        if (mode === "edit" && onContactUpdated) {
          onContactUpdated(result.data)
        } else if (mode === "create" && onContactCreated) {
          onContactCreated(result.data)
        }

        // Reset form and close dialog
        resetForm()
        onOpenChange(false)
      } else {
        throw new Error(result.message || "Failed to save contact")
      }
    } catch (error: any) {
      toast({
        title: error.code || "Error",
        description: error.message || "Failed to save contact",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
      setSubmitting(false)
    }

  }

  // Called from VerifyPreview "Create Contact" button
  const handleConfirmCreate = async () => {
    if (!pendingValues) return
    setIsSubmitting(true)
    const payload = {
      ...pendingValues,
      dateOfLastContact: new Date(pendingValues.dateOfLastContact).getTime() || undefined,
      stats: verifyResponse
    }
    try {
      const result = await apiFetch("/api/contacts", {
        method: "POST",
        data: payload,
      })
      if (result.error === false) {
        toast({ title: "Success", description: "Contact created successfully" })
        onContactCreated?.(result.data)
        setVerifyResponse(null)
        setPendingValues(null)
        onOpenChange(false)
      } else {
        throw new Error(result.message || "Failed to create contact")
      }
    } catch (error) {
      toast({
        title: "Error",
        description: getApiErrorMessage(error, "Failed to create contact"),
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const isShowingPreview = mode === "create" && verifyResponse !== null && pendingValues !== null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="
        w-[60vw]
        max-w-2xl
        h-[90vh]
        overflow-hidden
        p-0
        gap-0
        flex flex-col
      "
      >
        <DialogHeader className="px-5 py-4">
          <DialogTitle>
            {mode === "edit"
              ? "Edit Contact"
              : isShowingPreview
                ? "Review & Confirm Contact"
                : "Create New Contact"}
          </DialogTitle>
        </DialogHeader>


        <Formik
          initialValues={getInitialValues()}
          validationSchema={ContactSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          key={`${mode}-${contact?._id || "new"}-${open}`}
        >
          {({ values, errors, touched, handleChange, setFieldValue, resetForm, isSubmitting: formikSubmitting }) => (
            <Form className="flex flex-col h-full overflow-hidden">
              <div className="flex-1 overflow-y-auto px-6 pb-6">
                {isShowingPreview ? (
                  // ── Preview screen ─────────────────────────────────────────────────
                  <VerifyPreview
                    values={pendingValues!}
                    verifyData={verifyResponse!}
                    statusOptionsList={statusOptionsList}
                    sourceOptionsList={sourceOptionsList}
                    onBack={() => {
                      setVerifyResponse(null)
                      setPendingValues(null)
                    }}
                    onConfirm={handleConfirmCreate}
                    isSubmitting={isSubmitting}
                  />
                ) : (
                  // ── Form screen ────────────────────────────────────────────────────
                  <ContactFormBody
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    resetForm={resetForm}
                    isSubmitting={isSubmitting || formikSubmitting}
                    onCancel={() => {
                      resetForm()
                      onOpenChange(false)
                    }}
                    mode={mode}
                    statusOptionsList={statusOptionsList}
                    sourceOptionsList={sourceOptionsList}
                  />
                )}
              </div>
            </Form>
          )}
        </Formik>

      </DialogContent>
    </Dialog>
  )
}